import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { BrandLogo } from "./BrandLogo";

const DEFAULT_NAV = [{ label: "Home", url: "/" }, { label: "About", url: "/about" }, { label: "Contact", url: "/contact" }];
const isExternal = (u) => /^(https?:|mailto:|tel:)/.test(u || "");

export function SmartLink({ to, newTab, className, children, onClick, ...rest }) {
  if (isExternal(to) || newTab) return <a href={to} target={newTab ? "_blank" : undefined} rel="noreferrer" className={typeof className === "function" ? className({ isActive: false }) : className} onClick={onClick} {...rest}>{children}</a>;
  return <NavLink to={to} end={to === "/"} className={className} onClick={onClick} {...rest}>{children}</NavLink>;
}

export default function PublicHeader({ layout: override, preview = false }) {
  const { user } = useAuth();
  const { general, layout: ctxLayout } = useSettings();
  const layout = override || ctxLayout;
  const authed = !preview && user && typeof user === "object";
  const [open, setOpen] = useState(false);
  const links = layout?.nav?.length ? layout.nav : (layout ? [] : DEFAULT_NAV);
  const cta = layout?.header_cta || { show_signin: true, label: "Get started", url: "/register" };

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive && !preview ? "text-blue-600" : "text-slate-600 hover:text-slate-900"}`;
  const ctaBtn = "bg-blue-600 hover:bg-blue-700 text-white shadow-[0_4px_14px_0_rgba(59,130,246,0.39)]";

  return (
    <header className={`${preview ? "" : "sticky top-0"} z-40 bg-white/90 backdrop-blur-md border-b border-slate-200`} data-testid={preview ? "preview-header" : "public-header"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="public-logo-link">
          <BrandLogo logoUrl={general.logo_url} siteName={general.site_name || "Fx Cloud"} textClass="text-slate-900" />
        </Link>

        <nav className={`${preview ? "flex" : "hidden md:flex"} items-center gap-8`}>
          {links.map((l, i) => (
            <SmartLink key={i} to={l.url} newTab={l.new_tab} className={linkClass} data-testid={`nav-${(l.label || "").toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</SmartLink>
          ))}
        </nav>

        <div className={`${preview ? "flex" : "hidden md:flex"} items-center gap-3`}>
          {authed ? (
            <Link to="/dashboard"><Button data-testid="header-dashboard-btn" className={ctaBtn}>Dashboard</Button></Link>
          ) : (
            <>
              {cta.show_signin && <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900 px-2" data-testid="header-signin">Sign in</Link>}
              {cta.label && <SmartLink to={cta.url || "/register"}><Button data-testid="header-getstarted" className={ctaBtn}>{cta.label}</Button></SmartLink>}
            </>
          )}
        </div>

        {!preview && (
          <button className="md:hidden text-slate-700" onClick={() => setOpen((o) => !o)} data-testid="public-mobile-menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        )}
      </div>

      {open && !preview && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3" data-testid="public-mobile-nav">
          {links.map((l, i) => (
            <SmartLink key={i} to={l.url} newTab={l.new_tab} onClick={() => setOpen(false)} className="block text-slate-700 font-medium py-1">{l.label}</SmartLink>
          ))}
          <div className="pt-2 flex gap-3">
            {authed ? (
              <Link to="/dashboard" className="flex-1"><Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">Dashboard</Button></Link>
            ) : (
              <>
                {cta.show_signin && <Link to="/login" className="flex-1"><Button variant="outline" className="w-full border-slate-300 text-slate-700">Sign in</Button></Link>}
                {cta.label && <SmartLink to={cta.url || "/register"} className="flex-1"><Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">{cta.label}</Button></SmartLink>}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
